/* CatalogTab — global Catalog Management for /admin.
 *
 * Three sub-tabs (one per division). Division admins see only their own.
 * Super admin sees all 3.
 *
 * Backed by:
 *   - /api/admin/systems/services        (Systems custom services)
 *   - /api/admin/services/custom-products (Services e-store products)
 *   - /api/academy/admin/programs        (Academy custom programs — existing)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Boxes, Wrench, ShoppingBag, GraduationCap, Plus, Pencil, Trash2, Eye, EyeOff,
  RefreshCw, X, Search, AlertTriangle, Crown, Lock, Image as ImageIcon, ExternalLink,
  Copy, Check,
} from 'lucide-react';
import CopyPaymentLinkButton from './CopyPaymentLinkButton';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const DIVISION_TABS = [
  { id: 'systems',  label: 'Systems Services', Icon: Wrench,        tone: 'orange' },
  { id: 'services', label: 'E-store Products', Icon: ShoppingBag,   tone: 'amber' },
  { id: 'academy',  label: 'Academy Programs', Icon: GraduationCap, tone: 'purple' },
  { id: 'images',   label: 'Image Library',    Icon: ImageIcon,     tone: 'slate', superOnly: true },
];

function Pill({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.08em] ${className}`}>
      {children}
    </span>
  );
}

function ConfirmModal({ title, body, busy, onCancel, onConfirm, confirmLabel = 'Delete', danger = true }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="catalog-confirm-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className={`flex-shrink-0 rounded-full p-2.5 ${danger ? 'bg-red-100' : 'bg-amber-100'}`}>
            <AlertTriangle className={`h-5 w-5 ${danger ? 'text-red-600' : 'text-amber-700'}`} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">{body}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <button onClick={onCancel} className="rounded-full px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="catalog-confirm-go"
            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
          >
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Systems custom services ----------

function SystemsCatalog({ scope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  // Stripe Payment Links per (slug, tier) — fetched once, cached client-side.
  const [payLinks, setPayLinks] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/systems/services');
      setRows(data.rows || []);
    } catch (e) {
      setToast(e.response?.data?.detail || 'Failed to load systems services');
    } finally { setLoading(false); }
  }, []);
  // Pull the Systems payment-link map. Failures are silent — the Copy button
  // renders an inert "No link" pill if the URL isn't there yet.
  const loadLinks = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/stripe/payment-links?division=systems');
      setPayLinks(data?.systems || {});
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); loadLinks(); }, [load, loadLinks]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const term = q.toLowerCase();
    return rows.filter((r) => `${r.name} ${r.slug} ${r.category}`.toLowerCase().includes(term));
  }, [rows, q]);

  const toggleDisabled = async (r) => {
    setBusy(`toggle-${r.slug}`);
    try {
      await api.patch(`/api/admin/systems/services/${r.slug}`, { disabled: !r.disabled });
      load();
    } catch (e) { setToast(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(''); }
  };
  const doDelete = async () => {
    if (!confirmTarget) return;
    setBusy('delete');
    try {
      await api.delete(`/api/admin/systems/services/${confirmTarget.slug}`);
      setToast(`Deleted "${confirmTarget.name}"`);
      setConfirmTarget(null);
      load();
    } catch (e) { setToast(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(''); }
  };

  return (
    <div data-testid="catalog-systems">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, slug or category…"
            data-testid="systems-search"
            className="w-full rounded-lg border border-gray-200 pl-10 pr-3 py-2 text-sm focus:border-[#c9a227] focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={() => setEditTarget({})}
            data-testid="systems-add"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-[#0e0e10] hover:-translate-y-0.5 transition-transform"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
          >
            <Plus className="h-3.5 w-3.5" /> Add service
          </button>
        </div>
      </div>

      {toast && <div className="rounded-xl border border-gray-200 bg-gray-900 px-4 py-2.5 text-sm text-white mb-3">{toast}</div>}

      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.1em] text-gray-600">
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Tiers</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">{loading ? 'Loading…' : 'No services match.'}</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.slug} data-testid={`systems-row-${r.slug}`} className={r.disabled ? 'bg-red-50/30' : 'hover:bg-gray-50'}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {r.image_url
                      ? <img src={r.image_url} alt="" className="h-10 w-10 object-cover rounded-lg border border-gray-200 shrink-0" />
                      : <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0"><Wrench className="h-4 w-4 text-orange-600" /></div>}
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                        {r.name}
                        {r.is_builtin && <Pill className="bg-slate-100 text-slate-700 border-slate-200"><Lock className="h-2.5 w-2.5" /> Built-in</Pill>}
                      </div>
                      <div className="text-[12px] text-gray-500 font-mono truncate">{r.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><Pill className="bg-orange-50 text-orange-800 border-orange-200">{r.category}</Pill></td>
                <td className="px-4 py-3 text-[12px]">
                  {(r.tiers || []).map((t) => `${t.name} ($${t.price})`).join(' · ')}
                </td>
                <td className="px-4 py-3">
                  {r.disabled
                    ? <Pill className="bg-red-100 text-red-800 border-red-300"><EyeOff className="h-3 w-3" /> Hidden</Pill>
                    : <Pill className="bg-emerald-100 text-emerald-800 border-emerald-200"><Eye className="h-3 w-3" /> Visible</Pill>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    {payLinks[r.slug] && (
                      <CopyPaymentLinkButton
                        tiers={payLinks[r.slug]}
                        slug={r.slug}
                        testIdPrefix="systems-paylink"
                      />
                    )}
                    <button onClick={() => toggleDisabled(r)} disabled={busy === `toggle-${r.slug}`}
                      data-testid={`systems-toggle-${r.slug}`}
                      title={r.disabled ? 'Show on public catalog' : 'Hide from public catalog'}
                      className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-[11.5px] font-semibold text-gray-800 hover:bg-gray-200">
                      {r.disabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {r.disabled ? 'Show' : 'Hide'}
                    </button>
                    {!r.is_builtin && (
                      <>
                        <button onClick={() => setEditTarget(r)}
                          data-testid={`systems-edit-${r.slug}`}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1.5 text-[11.5px] font-semibold text-blue-900 hover:bg-blue-200">
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button onClick={() => setConfirmTarget(r)}
                          data-testid={`systems-delete-${r.slug}`}
                          className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1.5 text-[11.5px] font-semibold text-red-900 hover:bg-red-200">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <SystemsServiceModal
          initial={editTarget?.slug ? editTarget : null}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); setToast('Saved.'); setTimeout(() => setToast(''), 2500); }}
        />
      )}
      {confirmTarget && (
        <ConfirmModal
          title={`Delete "${confirmTarget.name}"?`}
          body="This permanently removes the custom service from the public catalog. Built-in services cannot be deleted."
          busy={busy === 'delete'}
          onCancel={() => setConfirmTarget(null)}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
}

function SystemsServiceModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    slug: initial?.slug || '',
    name: initial?.name || '',
    category: initial?.category || 'CUSTOM',
    tagline: initial?.tagline || '',
    description: initial?.description || '',
    cta_type: initial?.cta_type || 'consultation',
    billing: initial?.billing || 'subscription',
    unit: initial?.unit || 'month',
    image_url: initial?.image_url || '',
    includes: (initial?.includes || []).join('\n'),
    tiers: initial?.tiers?.length ? initial.tiers : [
      { id: 'standard', name: 'Standard', price: 99, description: '', features: [], popular: false },
    ],
    popular: !!initial?.popular,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setTier = (i, key, val) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t) }));
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, { id: `tier-${f.tiers.length + 1}`, name: '', price: 0, description: '', features: [], popular: false }] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      const payload = {
        ...form,
        includes: form.includes.split('\n').map((s) => s.trim()).filter(Boolean),
        tiers: form.tiers.map((t) => ({
          ...t,
          price: Number(t.price) || 0,
          features: typeof t.features === 'string' ? t.features.split('\n').map((s) => s.trim()).filter(Boolean) : (t.features || []),
        })),
      };
      if (isEdit) {
        await api.patch(`/api/admin/systems/services/${initial.slug}`, payload);
      } else {
        await api.post('/api/admin/systems/services', payload);
      }
      onSaved();
    } catch (er) {
      setErr(er.response?.data?.detail || er.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto" data-testid="systems-modal">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white p-6 my-8 shadow-2xl space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit service' : 'Add new Systems service'}</h3>
            <p className="text-sm text-gray-500">{isEdit ? `Slug: ${initial.slug}` : 'Create a new offering for ZHELTA Systems.'}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name *">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="systems-form-name" className="form-input" />
          </Field>
          <Field label="Category">
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="form-input" />
          </Field>
          {!isEdit && (
            <Field label="Slug (auto-generated if empty)">
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="e.g. drone-surveying" className="form-input" />
            </Field>
          )}
          <Field label="Billing">
            <select value={form.billing} onChange={(e) => setForm({ ...form, billing: e.target.value })} className="form-input">
              <option value="subscription">Subscription</option>
              <option value="one_time">One-time</option>
              <option value="per_session">Per session</option>
            </select>
          </Field>
          <Field label="Unit (e.g. month, device)">
            <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="form-input" />
          </Field>
          <Field label="CTA type">
            <select value={form.cta_type} onChange={(e) => setForm({ ...form, cta_type: e.target.value })} className="form-input">
              <option value="consultation">Book a Consultation</option>
              <option value="demo">Book a Demo</option>
            </select>
          </Field>
        </div>

        <Field label="Tagline">
          <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="form-input" />
        </Field>
        <Field label="Description">
          <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-input" />
        </Field>
        <Field label="Hero image">
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setForm((s) => ({ ...s, image_url: url }))}
            testid="systems-image"
          />
        </Field>
        <Field label="What's included (one per line)">
          <textarea rows={3} value={form.includes} onChange={(e) => setForm({ ...form, includes: e.target.value })} className="form-input font-mono text-[12px]" />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-600">Pricing tiers</div>
            <button type="button" onClick={addTier} className="text-[11px] font-semibold text-amber-700 hover:text-amber-900">+ Add tier</button>
          </div>
          <div className="space-y-2">
            {form.tiers.map((t, i) => (
              <div key={i} className="rounded-xl border border-gray-200 p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-start">
                <input placeholder="Tier id (e.g. essential)" value={t.id} onChange={(e) => setTier(i, 'id', e.target.value)} className="form-input" />
                <input placeholder="Tier name" value={t.name} onChange={(e) => setTier(i, 'name', e.target.value)} className="form-input" />
                <input placeholder="Price" type="number" min="0" step="0.01" value={t.price} onChange={(e) => setTier(i, 'price', e.target.value)} className="form-input" />
                <button type="button" onClick={() => removeTier(i)} disabled={form.tiers.length === 1} className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
                <input placeholder="Short description" value={t.description || ''} onChange={(e) => setTier(i, 'description', e.target.value)} className="form-input md:col-span-3" />
                <textarea placeholder="Features (one per line)" rows={2} value={Array.isArray(t.features) ? t.features.join('\n') : (t.features || '')} onChange={(e) => setTier(i, 'features', e.target.value)} className="form-input md:col-span-4 font-mono text-[12px]" />
              </div>
            ))}
          </div>
        </div>

        {err && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</div>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">Cancel</button>
          <button type="submit" disabled={saving} data-testid="systems-form-submit" className="rounded-full px-6 py-2 text-sm font-semibold text-[#0e0e10] disabled:opacity-60" style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create service')}
          </button>
        </div>
        <style>{`.form-input{width:100%;border:1px solid #e5e7eb;border-radius:8px;padding:8px 12px;font-size:13px;outline:none}.form-input:focus{border-color:#c9a227}`}</style>
      </form>
    </div>
  );
}

// ---------- Services e-store products ----------

function ServicesCatalog({ scope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [payLinks, setPayLinks] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/services/custom-products');
      setRows(data.rows || []);
    } catch (e) { setToast(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  const loadLinks = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/stripe/payment-links?division=services');
      setPayLinks(data?.services || {});
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); loadLinks(); }, [load, loadLinks]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const term = q.toLowerCase();
    return rows.filter((r) => `${r.name} ${r.slug} ${r.category}`.toLowerCase().includes(term));
  }, [rows, q]);

  const toggleDisabled = async (r) => {
    setBusy(`t-${r.slug}`);
    try { await api.patch(`/api/admin/services/custom-products/${r.slug}`, { disabled: !r.disabled }); load(); }
    catch (e) { setToast(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(''); }
  };
  const doDelete = async () => {
    if (!confirmTarget) return;
    setBusy('delete');
    try { await api.delete(`/api/admin/services/custom-products/${confirmTarget.slug}`); setConfirmTarget(null); load(); }
    catch (e) { setToast(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(''); }
  };

  return (
    <div data-testid="catalog-services">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="services-search"
            className="w-full rounded-lg border border-gray-200 pl-10 pr-3 py-2 text-sm focus:border-[#c9a227] focus:outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={() => setEditTarget({})} data-testid="services-add"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            <Plus className="h-3.5 w-3.5" /> Add product
          </button>
        </div>
      </div>
      {toast && <div className="rounded-xl border border-gray-200 bg-gray-900 px-4 py-2.5 text-sm text-white mb-3">{toast}</div>}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.1em] text-gray-600">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">{loading ? 'Loading…' : 'No products yet — click "Add product".'}</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.slug} data-testid={`services-row-${r.slug}`} className={r.disabled ? 'bg-red-50/30' : 'hover:bg-gray-50'}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {r.image
                      ? <img src={r.image} alt="" className="h-10 w-10 object-cover rounded-lg border border-gray-200" />
                      : <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center"><ImageIcon className="h-4 w-4 text-gray-400" /></div>}
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                        {r.name}
                        {r.featured && <Pill className="bg-amber-100 text-amber-800 border-amber-200">Featured</Pill>}
                        {r.badge && <Pill className="bg-blue-100 text-blue-800 border-blue-200">{r.badge}</Pill>}
                      </div>
                      <div className="text-[12px] text-gray-500 font-mono truncate">{r.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold">${Number(r.price).toFixed(2)}</div>
                  {r.original_price > r.price && (
                    <div className="text-[11px] line-through text-gray-400">${Number(r.original_price).toFixed(2)}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Pill className={r.stock > 5 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.stock > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}>
                    {r.stock} in stock
                  </Pill>
                </td>
                <td className="px-4 py-3">
                  {r.disabled
                    ? <Pill className="bg-red-100 text-red-800 border-red-300"><EyeOff className="h-3 w-3" /> Hidden</Pill>
                    : <Pill className="bg-emerald-100 text-emerald-800 border-emerald-200"><Eye className="h-3 w-3" /> Live</Pill>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    <CopyPaymentLinkButton
                      url={payLinks[r.slug]?.base?.url}
                      slug={r.slug}
                      testIdPrefix="services-paylink"
                    />
                    {r.cta_url && <a href={r.cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1.5 text-[11px] font-semibold text-gray-800 hover:bg-gray-200" title="Open buy link"><ExternalLink className="h-3 w-3" /></a>}
                    <button onClick={() => toggleDisabled(r)} disabled={busy === `t-${r.slug}`} data-testid={`services-toggle-${r.slug}`}
                      className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-[11.5px] font-semibold text-gray-800 hover:bg-gray-200">
                      {r.disabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {r.disabled ? 'Show' : 'Hide'}
                    </button>
                    <button onClick={() => setEditTarget(r)} data-testid={`services-edit-${r.slug}`}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1.5 text-[11.5px] font-semibold text-blue-900 hover:bg-blue-200">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button onClick={() => setConfirmTarget(r)} data-testid={`services-delete-${r.slug}`}
                      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1.5 text-[11.5px] font-semibold text-red-900 hover:bg-red-200">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <ServicesProductModal
          initial={editTarget?.slug ? editTarget : null}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); setToast('Saved.'); setTimeout(() => setToast(''), 2500); }}
        />
      )}
      {confirmTarget && (
        <ConfirmModal title={`Delete "${confirmTarget.name}"?`} body="This permanently removes the product from the e-store."
          busy={busy === 'delete'} onCancel={() => setConfirmTarget(null)} onConfirm={doDelete} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

/** ImageUploadField — drag-and-drop OR URL paste, with live preview.
 *  Hits POST /api/admin/catalog/images and autofills the returned URL. */
function ImageUploadField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [drag, setDrag] = useState(false);

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { setErr('Please select an image file'); return; }
    if (file.size > 4 * 1024 * 1024) { setErr('Max 4MB'); return; }
    setErr(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/admin/catalog/images', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Upload failed');
    } finally { setBusy(false); }
  }, [onChange]);

  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer?.files?.[0]); }}
        className={`relative rounded-xl border-2 border-dashed p-3 transition-colors ${
          drag ? 'border-[#c9a227] bg-[#fffaeb]' : 'border-gray-200 hover:border-[#c9a227]'
        }`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          {value ? (
            <img src={value} alt="" className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-gray-100 flex items-center justify-center">
              <ImageIcon className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Paste an image URL or drop a file"
              className="form-input"
              data-testid={`${testid}-url`}
            />
            <div className="mt-2 flex items-center gap-2">
              <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider cursor-pointer ${busy ? 'bg-gray-100 text-gray-400' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}>
                {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                {busy ? 'Uploading…' : 'Upload image'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  data-testid={`${testid}-file`}
                  onChange={(e) => upload(e.target.files?.[0])}
                />
              </label>
              {value && (
                <button type="button" onClick={() => onChange('')} className="text-[11px] font-semibold text-gray-500 hover:text-red-700">Clear</button>
              )}
            </div>
          </div>
        </div>
        {err && <div className="mt-2 text-[11.5px] text-red-700">{err}</div>}
      </div>
    </div>
  );
}

function ServicesProductModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    slug: initial?.slug || '',
    name: initial?.name || '',
    category: initial?.category || 'accessories',
    description: initial?.description || '',
    price: initial?.price ?? 0,
    original_price: initial?.original_price ?? '',
    image: initial?.image || '',
    stock: initial?.stock ?? 0,
    featured: !!initial?.featured,
    badge: initial?.badge || '',
    rating: initial?.rating ?? 4.8,
    reviews: initial?.reviews ?? 0,
    cta_url: initial?.cta_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      const payload = {
        ...f,
        price: Number(f.price) || 0,
        original_price: f.original_price === '' ? null : Number(f.original_price),
        stock: Number(f.stock) || 0,
        rating: Number(f.rating) || 0,
        reviews: Number(f.reviews) || 0,
      };
      if (isEdit) await api.patch(`/api/admin/services/custom-products/${initial.slug}`, payload);
      else await api.post('/api/admin/services/custom-products', payload);
      onSaved();
    } catch (er) { setErr(er.response?.data?.detail || er.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto" data-testid="services-modal">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white p-6 my-8 shadow-2xl space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit product' : 'Add new e-store product'}</h3>
            <p className="text-sm text-gray-500">{isEdit ? `Slug: ${initial.slug}` : 'Create a new ZHELTA-managed product.'}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name *"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="services-form-name" className="form-input" /></Field>
          <Field label="Category"><input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="form-input" /></Field>
          {!isEdit && <Field label="Slug (auto-generated if empty)"><input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="form-input" /></Field>}
          <Field label="Price ($) *"><input required type="number" min="0" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="services-form-price" className="form-input" /></Field>
          <Field label="Original price (optional, for strike-through)"><input type="number" min="0" step="0.01" value={f.original_price} onChange={(e) => setF({ ...f, original_price: e.target.value })} className="form-input" /></Field>
          <Field label="Stock"><input type="number" min="0" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className="form-input" /></Field>
          <Field label="Image URL">
            <ImageUploadField
              value={f.image}
              onChange={(url) => setF((s) => ({ ...s, image: url }))}
              testid="services-image"
            />
            {/* Unsplash-placeholder nudge — flags stock imagery that the
                super-admin should replace with their own product photo. */}
            {f.image && f.image.includes("images.unsplash.com") && (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2" data-testid="image-unsplash-warning">
                <span className="text-base leading-none">⚠️</span>
                <div>
                  <div className="font-bold">This is a generic Unsplash placeholder.</div>
                  <div className="text-[11px] text-amber-800">
                    Replace with a real product photo for better conversion. Drop an image above or paste a direct URL.
                  </div>
                </div>
              </div>
            )}
          </Field>
          <Field label="Buy link (cta_url)"><input value={f.cta_url} onChange={(e) => setF({ ...f, cta_url: e.target.value })} placeholder="https://cart-central…" className="form-input" /></Field>
          <Field label="Badge (e.g. NEW, SALE)"><input value={f.badge} onChange={(e) => setF({ ...f, badge: e.target.value })} className="form-input" /></Field>
          <Field label="Rating (0-5)"><input type="number" min="0" max="5" step="0.1" value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} className="form-input" /></Field>
          <Field label="Reviews count"><input type="number" min="0" value={f.reviews} onChange={(e) => setF({ ...f, reviews: e.target.value })} className="form-input" /></Field>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="h-4 w-4 rounded border-gray-300" />
            <span className="text-sm font-semibold text-gray-700">Featured (pin to top of public rail)</span>
          </label>
        </div>
        <Field label="Description"><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="form-input" /></Field>
        {err && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</div>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">Cancel</button>
          <button type="submit" disabled={saving} data-testid="services-form-submit" className="rounded-full px-6 py-2 text-sm font-semibold text-[#0e0e10] disabled:opacity-60" style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create product')}
          </button>
        </div>
        <style>{`.form-input{width:100%;border:1px solid #e5e7eb;border-radius:8px;padding:8px 12px;font-size:13px;outline:none}.form-input:focus{border-color:#c9a227}`}</style>
      </form>
    </div>
  );
}

// ---------- Academy programs (delegates to existing AcademyAdminPanel pointer) ----------

function AcademyCatalogPointer() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-purple-100 p-2.5 flex-shrink-0">
          <GraduationCap className="h-5 w-5 text-purple-700" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900">Academy programs are managed here →</h3>
          <p className="text-sm text-gray-500 mt-1">
            Open <strong>Academy → Programs</strong> tab to add, edit and delete custom programs (with photo upload, starter modules and bulk enroll).
            Built-in programs cannot be removed but can be edited where applicable.
          </p>
          <a href="/admin?tab=academy" data-testid="catalog-academy-link"
             className="mt-4 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-[#0e0e10]"
             style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            Open Academy programs <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------- Image library ----------

function ImageLibrary() {
  const [data, setData] = useState({ rows: [], count: 0, total_bytes: 0, orphan_count: 0, orphan_bytes: 0 });
  const [purge, setPurge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'orphans' | 'used'
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: imgs }, { data: pv }] = await Promise.all([
        api.get('/api/admin/catalog/images'),
        api.get('/api/admin/catalog/image-purge?min_age_days=30'),
      ]);
      setData(imgs);
      setPurge(pv);
    } catch (e) { setToast(e.response?.data?.detail || 'Failed to load images'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (filter === 'orphans') return data.rows.filter((r) => r.is_orphan);
    if (filter === 'used') return data.rows.filter((r) => !r.is_orphan);
    return data.rows;
  }, [data.rows, filter]);

  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const copyUrl = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* ignore */ }
  };

  const doDelete = async () => {
    if (!confirmTarget) return;
    setBusy('delete');
    const force = (confirmTarget.used_by || []).length > 0;
    try {
      await api.delete(`/api/admin/catalog/images/${confirmTarget.id}${force ? '?force=true' : ''}`);
      setToast(force
        ? `Deleted — cleared ${confirmTarget.used_by.length} reference(s).`
        : 'Image deleted.');
      setConfirmTarget(null);
      load();
    } catch (e) { setToast(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(''); }
  };

  const purgeAllOrphans = async () => {
    const orphans = data.rows.filter((r) => r.is_orphan);
    if (!orphans.length) return;
    if (!window.confirm(`Permanently delete ${orphans.length} orphan image(s) and reclaim ${fmtBytes(data.orphan_bytes)}?`)) return;
    setBusy('purge');
    let ok = 0;
    for (const o of orphans) {
      try {
        await api.delete(`/api/admin/catalog/images/${o.id}`);
        ok += 1;
      } catch { /* keep going on individual failures */ }
    }
    setToast(`Purged ${ok} of ${orphans.length} orphans.`);
    setBusy('');
    load();
  };

  return (
    <div data-testid="catalog-images">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-1">
              {data.count} uploaded image{data.count === 1 ? '' : 's'} · {fmtBytes(data.total_bytes)} total
            </h3>
            <p className="text-[12.5px] text-gray-500">
              {data.orphan_count > 0
                ? <><span className="text-amber-700 font-bold">{data.orphan_count} orphan{data.orphan_count === 1 ? '' : 's'}</span> ({fmtBytes(data.orphan_bytes)}) — unused by any product or service.</>
                : 'Every uploaded image is currently in use. Storage is healthy.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { id: 'all', label: `All (${data.count})` },
              { id: 'orphans', label: `Orphans (${data.orphan_count})` },
              { id: 'used', label: `In use (${data.count - data.orphan_count})` },
            ].map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`images-filter-${f.id}`}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-[0.08em] border transition-colors ${
                  filter === f.id ? 'bg-[#0e0e10] text-white border-[#0e0e10]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#c9a227]'
                }`}>
                {f.label}
              </button>
            ))}
            <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-4 py-1.5 text-[11.5px] font-bold uppercase tracking-wider text-gray-700 hover:bg-gray-200">
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            {data.orphan_count > 0 && (
              <button onClick={purgeAllOrphans} disabled={busy === 'purge'} data-testid="images-purge-orphans"
                className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-4 py-1.5 text-[11.5px] font-bold uppercase tracking-wider text-red-900 hover:bg-red-200 disabled:opacity-60">
                <Trash2 className="h-3 w-3" />
                {busy === 'purge' ? 'Purging…' : `Purge all orphans`}
              </button>
            )}
          </div>
        </div>
      </div>

      {toast && <div className="rounded-xl border border-gray-200 bg-gray-900 px-4 py-2.5 text-sm text-white mb-3">{toast}</div>}

      {purge && purge.schedule?.enabled && (
        <div data-testid="catalog-image-autopurge" className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3 mb-4 text-[12px] text-amber-900 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Trash2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" />
            <div>
              <strong>Auto-purge cron:</strong> daily at {String(purge.schedule.hour).padStart(2, '0')}:00 {purge.schedule.tz} —
              deletes orphan images older than <strong>{purge.schedule.default_days} days</strong>.
              {purge.purgeable_count > 0
                ? <> Right now <strong>{purge.purgeable_count}</strong> image{purge.purgeable_count === 1 ? '' : 's'} would be reclaimed (<strong>{(purge.reclaimable_bytes / 1024).toFixed(1)} KB</strong>).</>
                : <> Nothing eligible right now — storage is healthy.</>}
              {purge.last_run && (
                <span className="ml-1 text-amber-800/70">
                  Last run {new Date(purge.last_run.ts).toLocaleString()} · {purge.last_run.status === 'purged' ? `${purge.last_run.purged_count} purged` : purge.last_run.status === 'skipped_no_orphans' ? 'no orphans' : 'error'}.
                </span>
              )}
            </div>
          </div>
          <button
            onClick={async () => {
              setBusy('autopurge');
              try {
                await api.post(`/api/admin/catalog/image-purge/send-now?min_age_days=${purge.schedule.default_days}`);
                setToast('Auto-purge ran. Check the log strip above for results.');
                load();
              } catch (e) { setToast(e.response?.data?.detail || 'Purge failed'); }
              finally { setBusy(''); }
            }}
            disabled={busy === 'autopurge'}
            data-testid="catalog-image-autopurge-run"
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider text-amber-900 hover:bg-amber-200 disabled:opacity-60"
          >
            {busy === 'autopurge' ? 'Running…' : 'Run now'}
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-gray-400">
          {loading ? 'Loading…' : filter === 'orphans' ? 'No orphan images.' : filter === 'used' ? 'No images in use yet.' : 'No images uploaded yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {visible.map((img) => (
            <div key={img.id} data-testid={`image-card-${img.id}`} className={`rounded-xl border bg-white overflow-hidden ${img.is_orphan ? 'border-amber-300' : 'border-gray-200'}`}>
              <div className="relative aspect-square bg-gray-50">
                <img src={img.url} alt={img.filename} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                {img.is_orphan && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300">
                    Orphan
                  </span>
                )}
              </div>
              <div className="p-2.5 space-y-1.5">
                <div className="text-[11px] font-semibold text-gray-900 truncate" title={img.filename}>{img.filename || '(no filename)'}</div>
                <div className="text-[10px] text-gray-500 flex items-center justify-between">
                  <span>{fmtBytes(img.size || 0)}</span>
                  <span>{(img.uploaded_at || '').slice(0, 10)}</span>
                </div>
                {(img.used_by || []).length > 0 && (
                  <div className="text-[10px] text-gray-600">
                    Used by: {(img.used_by || []).map((u) => u.name || u.slug).slice(0, 2).join(', ')}
                    {img.used_by.length > 2 && <span className="text-gray-400"> +{img.used_by.length - 2}</span>}
                  </div>
                )}
                <div className="flex items-center gap-1 pt-1">
                  <button
                    onClick={() => copyUrl(img.url, img.id)}
                    data-testid={`image-copy-${img.id}`}
                    title="Copy URL"
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[10.5px] font-bold uppercase tracking-wider text-gray-800 hover:bg-gray-200"
                  >
                    {copied === img.id ? <><Check className="h-3 w-3 text-emerald-600" /> Copied</> : <><Copy className="h-3 w-3" /> URL</>}
                  </button>
                  <button
                    onClick={() => setConfirmTarget(img)}
                    data-testid={`image-delete-${img.id}`}
                    className="inline-flex items-center justify-center rounded-md bg-red-50 p-1.5 text-red-700 hover:bg-red-100"
                    title={img.is_orphan ? 'Delete' : 'Delete (force — clears references)'}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmTarget && (
        <ConfirmModal
          title={`Delete this image?`}
          body={(confirmTarget.used_by || []).length > 0
            ? `This image is currently used by ${confirmTarget.used_by.length} catalog item(s). Deleting it will null out the image field on those items so they show a placeholder until you re-upload.`
            : 'This orphan image is not referenced by any product or service.'}
          confirmLabel={(confirmTarget.used_by || []).length > 0 ? 'Force delete' : 'Delete'}
          busy={busy === 'delete'}
          onCancel={() => setConfirmTarget(null)}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
}

// ---------- Main tab ----------

export default function CatalogTab({ adminPassword }) {
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [scope, setScope] = useState('super');
  const [active, setActive] = useState('systems');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/auth/me');
        if (cancelled) return;
        setAuthed(true);
        // Use email and division to figure out scope so we filter sub-tabs.
        const isSuper = (data?.email || '').toLowerCase() === 'admin@zhelta.com';
        if (isSuper) setScope('super');
        else if (['systems', 'services', 'academy'].includes(data?.division)) setScope(data.division);
        else setScope('super');
        return;
      } catch { /* fall through */ }
      if (!adminPassword) { if (!cancelled) setAuthError('Sign in via /zhelta-internal/console first.'); return; }
      try {
        await api.post('/api/auth/login', { email: 'admin@zhelta.com', password: adminPassword });
        if (!cancelled) { setAuthed(true); setScope('super'); }
      } catch (e) { if (!cancelled) setAuthError(e.response?.data?.detail || 'Login failed'); }
    })();
    return () => { cancelled = true; };
  }, [adminPassword]);

  // Auto-pick the right sub-tab when scope is a single division.
  useEffect(() => {
    if (scope === 'systems') setActive('systems');
    else if (scope === 'services') setActive('services');
    else if (scope === 'academy') setActive('academy');
  }, [scope]);

  const visibleTabs = scope === 'super'
    ? DIVISION_TABS
    : DIVISION_TABS.filter((t) => t.id === scope && !t.superOnly);

  if (authError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-900">
        <div className="font-bold mb-1">Sign-in required</div>
        <div className="text-sm">{authError}</div>
      </div>
    );
  }
  if (!authed) {
    return <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-500 text-sm">Authenticating Catalog…</div>;
  }

  return (
    <div data-testid="catalog-tab" className="space-y-5">
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Boxes className="h-5 w-5 text-[#c9a227]" />
              <h2 className="text-lg font-bold text-gray-900">Catalog Management</h2>
              {scope === 'super'
                ? <Pill className="bg-yellow-100 text-yellow-900 border-yellow-300"><Crown className="h-3 w-3" /> Super Admin</Pill>
                : <Pill className="bg-gray-100 text-gray-700 border-gray-300">{scope.charAt(0).toUpperCase() + scope.slice(1)} Division</Pill>}
            </div>
            <p className="text-sm text-gray-500">
              {scope === 'super'
                ? 'Add, edit, hide and delete services, products and programs across all 3 divisions.'
                : `You can only manage the ${scope} catalog. Contact the Super Admin for cross-division changes.`}
            </p>
          </div>
        </div>
        {/* Sub-tabs */}
        {visibleTabs.length > 1 && (
          <div className="mt-4 flex items-center gap-1.5 flex-wrap">
            {visibleTabs.map((t) => (
              <button key={t.id} onClick={() => setActive(t.id)} data-testid={`catalog-subtab-${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.08em] transition-colors ${
                  active === t.id ? 'bg-[#0e0e10] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#c9a227]'
                }`}>
                <t.Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {active === 'systems'  && <SystemsCatalog scope={scope} />}
      {active === 'services' && <ServicesCatalog scope={scope} />}
      {active === 'academy'  && <AcademyCatalogPointer />}
      {active === 'images'   && <ImageLibrary />}
    </div>
  );
}
